import {
    IProfileAddAndUpdateRequest,
    ProfileDataObject,
    IProfileSettings,
    IWatchlistQueryParams,
    IWatchlistGetResponse,
    GetProfilesResponse,
    IB2CForgotParentalLockStep1Response,
    IB2CForgotParentalLockStep2Response,
    ICreateParentalLockRequest,
    ICreateParentalLockResponse,
    IValidateParentalLockResponse,
    IGetParentalLockResponse,
    IUpdateParentalLockResponse,
    IValidatePasswordResponse,
    IAccountB2CRetrievalGenerateOTPResponse,
    IAccountB2CRetrievalVerifyOTPResponse,
    IResetParentalLockVerifyOTPResponse,
} from '../../entity/interface/iProfile';
import { container } from '../../DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';

import { IAddProfileUseCase } from '../../core/usecases/profile/AddProfileUseCase';
import { IUpdateProfileUseCase } from '../../core/usecases/profile/UpdateProfileUseCase';
import { IDeleteProfileUseCase } from '../../core/usecases/profile/DeleteProfileUseCase';
import { IGetProfilesUseCase } from '../../core/usecases/profile/GetProfilesUseCase';
import { ISwitchProfileUseCase } from '../../core/usecases/profile/SwitchProfileUseCase';
import { IAddFavoriteChannelsUseCase } from '../../core/usecases/profile/AddFavoriteChannelsUseCase';
import { IGetFavoriteChannelsUseCase } from '../../core/usecases/profile/GetFavoriteChannelsUseCase';
import { IDeleteFavoriteChannelsUseCase } from '../../core/usecases/profile/DeleteFavoriteChannelsUseCase';
import { IAddWatchlistUseCase } from '../../core/usecases/profile/AddWatchlistUseCase';
import { IGetWatchlistUseCase } from '../../core/usecases/profile/GetWatchlistUseCase';
import { IDeleteWatchlistUseCase } from '../../core/usecases/profile/DeleteWatchlistUseCase';
import { IAccountRetrievalGenerateOTPUseCase } from '../../core/usecases/profile/AccountRetrievalGenerateOTPUseCase';
import { IAccountRetrievalVerifyOTPUseCase } from '../../core/usecases/profile/AccountRetrievalVerifyOTPUseCase';
import { ICreateParentalLockUseCase } from '../../core/usecases/profile/CreateParentalLockUseCase';
import { IValidateParentalLockUseCase } from '../../core/usecases/profile/ValidateParentalLockUseCase';
import { IGetParentalLockUseCase } from '../../core/usecases/profile/GetParentalLockUseCase';
import { IUpdateParentalLockUseCase } from '../../core/usecases/profile/UpdateParentalLockUseCase';
import { IValidatePasswordUseCase } from '../../core/usecases/profile/ValidatePasswordUseCase';
import { IFormSignatureObjectUseCase } from '../../core/usecases/auth/FormSignatureObjectUseCase';
import type { IProfileData } from '@intigral/types';
import { IAccountB2CRetrievalGenerateOTPUseCase } from '../../core/usecases/profile/AccountB2CRetrievalGenerateOTPUseCase';
import { IAccountB2CRetrievalVerifyOTPUseCase } from '../../core/usecases/profile/AccountB2CRetrievalVerifyOTPUseCase';
import { IResetParentalLockOTPUseCase } from '../../core/usecases/profile/ResetParentalLockOTPUseCase';
import { GUEST_PROFILE_GUID } from '@intigral/constant';

export interface IProfileService {
    addProfile(
        apiKey: string,
        profileName: string,
        profileSettings: IProfileSettings,
        kidsMode: boolean,
        token: string,
        authSign: string,
        identityToken: string
    ): Promise<any>;

    updateProfile(
        apiKey: string,
        guid: string,
        password: string | null,
        profileName: string,
        updatedProfileSettings: IProfileSettings,
        kidsMode: boolean | null,
        token: string,
        authSign: string,
        identityToken: string,
        currentProfile: IProfileData
    ): Promise<any>;

    deleteProfile(
        apiKey: string,
        guid: string,
        userPassword: string,
        token: string,
        authSign: string,
        identityToken: string,
        currentProfile: IProfileData
    ): Promise<any>;

    getProfiles(apiKey: string, token: string, identityToken: string, kidsMode?: boolean): Promise<GetProfilesResponse>;

    switchProfile(
        apiKey: string,
        profileGuid: string,
        currentProfile: IProfileData,
        profilePassword: string,
        token: string,
        identityToken: string
    ): Promise<any>;

    addFavoriteChannels(
        apiKey: string,
        profileGuid: string,
        channels: string[],
        token: string,
        identityToken: string
    ): Promise<string[]>;

    getFavoriteChannels(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        query: object
    ): Promise<string[]>;

    deleteFavoriteChannels(
        apiKey: string,
        profileGuid: string,
        ids: string[],
        token: string,
        identityToken: string
    ): Promise<boolean>;

    addWatchlist(
        type: 'linear' | 'vod',
        apiKey: string,
        profileGuid: string,
        ids: string[],
        token: string,
        identityToken: string
    ): Promise<string[]>;

    getWatchlist(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        query: IWatchlistQueryParams
    ): Promise<IWatchlistGetResponse['data']>;

    deleteWatchlist(
        type: 'linear' | 'vod',
        apiKey: string,
        profileGuid: string,
        ids: string[],
        token: string,
        identityToken: string
    ): Promise<boolean>;

    accountRetrievalGenerateOTP(
        apiKey: string,
        identityToken: string,
        token: string,
        username: string
    ): Promise<IB2CForgotParentalLockStep1Response['data']>;

    accountB2CRetrievalGenerateOTP(username: string): Promise<IAccountB2CRetrievalGenerateOTPResponse['data']>;

    accountRetrievalVerifyOTP(
        apiKey: string,
        identityToken: string,
        otp: string
    ): Promise<IB2CForgotParentalLockStep2Response['data']>;

    accountB2CRetrievalVerifyOTP(
        apiKey: string,
        otp: string,
        username: string,
        userId?: string,
        productKey?: string
    ): Promise<IAccountB2CRetrievalVerifyOTPResponse['data']>;

    createParentalLock(
        apiKey: string,
        identityToken: string,
        request: ICreateParentalLockRequest
    ): Promise<ICreateParentalLockResponse['data']>;

    validateParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encLock: string
    ): Promise<IValidateParentalLockResponse['data']>;

    getParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encPassword: string
    ): Promise<IGetParentalLockResponse['data']>;

    updateParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encPassword: string,
        newEncLock: string
    ): Promise<IUpdateParentalLockResponse['data']>;

    resetParentalLockGenerateOTP(
        apiKey: string,
        identityToken: string,
        username: string,
        lang: string
    ): Promise<IB2CForgotParentalLockStep1Response['data']>;

    resetParentalLockVerifyOTP(
        apiKey: string,
        identityToken: string,
        otp: string,
        verificationGuid: string
    ): Promise<IResetParentalLockVerifyOTPResponse['data']>;

    resetParentalLockWithOTP(
        apiKey: string,
        identityToken: string,
        pin: string,
        options?: { verificationGuid?: string; currentParentalLockPin?: string }
    ): Promise<IUpdateParentalLockResponse['data']>;

    validatePassword(
        authToken: string,
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encPw: string,
        username: string
    ): Promise<IValidatePasswordResponse['data']>;
}

export class ProfileService implements IProfileService {
    // Lazy getters for use cases to avoid circular dependencies
    private get addProfileUseCase(): IAddProfileUseCase {
        return container.resolve<IAddProfileUseCase>(DI.USECASES.IAddProfileUseCase);
    }

    private get updateProfileUseCase(): IUpdateProfileUseCase {
        return container.resolve<IUpdateProfileUseCase>(DI.USECASES.IUpdateProfileUseCase);
    }

    private get deleteProfileUseCase(): IDeleteProfileUseCase {
        return container.resolve<IDeleteProfileUseCase>(DI.USECASES.IDeleteProfileUseCase);
    }

    private get getProfilesUseCase(): IGetProfilesUseCase {
        return container.resolve<IGetProfilesUseCase>(DI.USECASES.IGetProfilesUseCase);
    }

    private get switchProfileUseCase(): ISwitchProfileUseCase {
        return container.resolve<ISwitchProfileUseCase>(DI.USECASES.ISwitchProfileUseCase);
    }

    private get addFavoriteChannelsUseCase(): IAddFavoriteChannelsUseCase {
        return container.resolve<IAddFavoriteChannelsUseCase>(DI.USECASES.IAddFavoriteChannelsUseCase);
    }

    private get getFavoriteChannelsUseCase(): IGetFavoriteChannelsUseCase {
        return container.resolve<IGetFavoriteChannelsUseCase>(DI.USECASES.IGetFavoriteChannelsUseCase);
    }

    private get deleteFavoriteChannelsUseCase(): IDeleteFavoriteChannelsUseCase {
        return container.resolve<IDeleteFavoriteChannelsUseCase>(DI.USECASES.IDeleteFavoriteChannelsUseCase);
    }

    private get addWatchlistUseCase(): IAddWatchlistUseCase {
        return container.resolve<IAddWatchlistUseCase>(DI.USECASES.IAddWatchlistUseCase);
    }

    private get getWatchlistUseCase(): IGetWatchlistUseCase {
        return container.resolve<IGetWatchlistUseCase>(DI.USECASES.IGetWatchlistUseCase);
    }

    private get deleteWatchlistUseCase(): IDeleteWatchlistUseCase {
        return container.resolve<IDeleteWatchlistUseCase>(DI.USECASES.IDeleteWatchlistUseCase);
    }

    private get accountRetrievalGenerateOTPUseCase(): IAccountRetrievalGenerateOTPUseCase {
        return container.resolve<IAccountRetrievalGenerateOTPUseCase>(DI.USECASES.IAccountRetrievalGenerateOTPUseCase);
    }

    private get accountB2CRetrievalGenerateOTPUseCase(): IAccountB2CRetrievalGenerateOTPUseCase {
        return container.resolve<IAccountB2CRetrievalGenerateOTPUseCase>(
            DI.USECASES.IAccountB2CRetrievalGenerateOTPUseCase
        );
    }

    private get accountRetrievalVerifyOTPUseCase(): IAccountRetrievalVerifyOTPUseCase {
        return container.resolve<IAccountRetrievalVerifyOTPUseCase>(DI.USECASES.IAccountRetrievalVerifyOTPUseCase);
    }

    private get accountB2CRetrievalVerifyOTPUseCase(): IAccountB2CRetrievalVerifyOTPUseCase {
        return container.resolve<IAccountB2CRetrievalVerifyOTPUseCase>(
            DI.USECASES.IAccountB2CRetrievalVerifyOTPUseCase
        );
    }

    private get createParentalLockUseCase(): ICreateParentalLockUseCase {
        return container.resolve<ICreateParentalLockUseCase>(DI.USECASES.ICreateParentalLockUseCase);
    }

    private get validateParentalLockUseCase(): IValidateParentalLockUseCase {
        return container.resolve<IValidateParentalLockUseCase>(DI.USECASES.IValidateParentalLockUseCase);
    }

    private get getParentalLockUseCase(): IGetParentalLockUseCase {
        return container.resolve<IGetParentalLockUseCase>(DI.USECASES.IGetParentalLockUseCase);
    }

    private get updateParentalLockUseCase(): IUpdateParentalLockUseCase {
        return container.resolve<IUpdateParentalLockUseCase>(DI.USECASES.IUpdateParentalLockUseCase);
    }

    private get resetParentalLockOTPUseCase(): IResetParentalLockOTPUseCase {
        return container.resolve<IResetParentalLockOTPUseCase>(DI.USECASES.IResetParentalLockOTPUseCase);
    }

    private get validatePasswordUseCase(): IValidatePasswordUseCase {
        return container.resolve<IValidatePasswordUseCase>(DI.USECASES.IValidatePasswordUseCase);
    }

    private get authUseCase(): IFormSignatureObjectUseCase {
        return container.resolve<IFormSignatureObjectUseCase>(DI.USECASES.IFormSignatureObjectUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async addProfile(
        apiKey: string,
        profileName: string,
        profileSettings: IProfileSettings,
        kidsMode: boolean,
        token: string,
        authSign: string,
        identityToken: string
    ): Promise<ProfileDataObject> {
        const apiToken = await this.authUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.addProfileUseCase.execute(
            apiKey,
            profileName,
            profileSettings,
            kidsMode,
            token,
            authSign,
            identityToken,
            apiToken
        );
    }

    async updateProfile(
        apiKey: string,
        guid: string,
        password: string,
        profileName: string,
        updatedProfileSettings: IProfileSettings,
        kidsMode: boolean,
        token: string,
        authSign: string,
        identityToken: string,
        currentProfile: IProfileData
    ): Promise<ProfileDataObject> {
        const apiToken = await this.authUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.updateProfileUseCase.execute(
            apiKey,
            guid,
            password,
            profileName,
            updatedProfileSettings,
            kidsMode,
            token,
            authSign,
            identityToken,
            currentProfile,
            apiToken
        );
    }

    async deleteProfile(
        apiKey: string,
        guid: string,
        userPassword: string,
        token: string,
        authSign: string,
        identityToken: string,
        currentProfile: IProfileData
    ): Promise<ProfileDataObject> {
        const csrfToken = await this.authUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        return await this.deleteProfileUseCase.execute(
            apiKey,
            guid,
            userPassword,
            token,
            authSign,
            identityToken,
            currentProfile,
            csrfToken as { [key: string]: string }
        );
    }

    async getProfiles(
        apiKey: string,
        token: string,
        identityToken: string,
        kidsMode?: boolean
    ): Promise<GetProfilesResponse> {
        return await this.getProfilesUseCase.execute(apiKey, token, identityToken, kidsMode === true);
    }

    async switchProfile(
        apiKey: string,
        profileGuid: string,
        currentProfile: IProfileData,
        profilePassword: string,
        token: string,
        identityToken: string
    ): Promise<string> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return '';
        }
        const apiToken = await this.authUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        return await this.switchProfileUseCase.execute(
            apiKey,
            profileGuid,
            currentProfile,
            profilePassword,
            token,
            identityToken
        );
    }

    async addFavoriteChannels(
        apiKey: string,
        profileGuid: string,
        channels: string[],
        token: string,
        identityToken: string
    ): Promise<string[]> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return [];
        }
        return await this.addFavoriteChannelsUseCase.execute(apiKey, profileGuid, channels, token, identityToken);
    }

    async getFavoriteChannels(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        query: object
    ): Promise<string[]> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return [];
        }
        return await this.getFavoriteChannelsUseCase.execute(apiKey, profileGuid, token, identityToken, query);
    }

    async deleteFavoriteChannels(
        apiKey: string,
        profileGuid: string,
        ids: string[],
        token: string,
        identityToken: string
    ): Promise<boolean> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return false;
        }
        return await this.deleteFavoriteChannelsUseCase.execute(apiKey, profileGuid, ids, token, identityToken);
    }

    async addWatchlist(
        type: 'linear' | 'vod',
        apiKey: string,
        profileGuid: string,
        ids: string[],
        token: string,
        identityToken: string
    ): Promise<string[]> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return [];
        }
        return await this.addWatchlistUseCase.execute(type, apiKey, profileGuid, ids, token, identityToken);
    }

    async getWatchlist(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        query: IWatchlistQueryParams
    ): Promise<IWatchlistGetResponse['data']> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return { totalCount: 0, pageSize: 0, pageNumber: 0, count: 0, entries: [] };
        }
        return await this.getWatchlistUseCase.execute(apiKey, profileGuid, token, identityToken, query);
    }

    async deleteWatchlist(
        type: 'linear' | 'vod',
        apiKey: string,
        profileGuid: string,
        ids: string[],
        token: string,
        identityToken: string
    ): Promise<boolean> {
        if (!profileGuid || profileGuid === GUEST_PROFILE_GUID) {
            return false;
        }
        return await this.deleteWatchlistUseCase.execute(type, apiKey, profileGuid, ids, token, identityToken);
    }

    async accountRetrievalGenerateOTP(
        apiKey: string,
        identityToken: string,
        token: string,
        username: string
    ): Promise<IB2CForgotParentalLockStep1Response['data']> {
        return await this.accountRetrievalGenerateOTPUseCase.execute(apiKey, identityToken, token, username);
    }
    async accountB2CRetrievalGenerateOTP(username: string): Promise<IAccountB2CRetrievalGenerateOTPResponse['data']> {
        return await this.accountB2CRetrievalGenerateOTPUseCase.execute(username);
    }

    async accountRetrievalVerifyOTP(
        apiKey: string,
        identityToken: string,
        otp: string
    ): Promise<IB2CForgotParentalLockStep2Response['data']> {
        return await this.accountRetrievalVerifyOTPUseCase.execute(apiKey, identityToken, otp);
    }

    async accountB2CRetrievalVerifyOTP(
        apiKey: string,
        otp: string,
        username: string,
        userId?: string,
        productKey?: string
    ): Promise<IAccountB2CRetrievalVerifyOTPResponse['data']> {
        return await this.accountB2CRetrievalVerifyOTPUseCase.execute(apiKey, otp, username, userId, productKey);
    }

    async createParentalLock(
        apiKey: string,
        identityToken: string,
        request: ICreateParentalLockRequest
    ): Promise<ICreateParentalLockResponse['data']> {
        return await this.createParentalLockUseCase.execute(apiKey, identityToken, request);
    }

    async validateParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encLock: string
    ): Promise<IValidateParentalLockResponse['data']> {
        return await this.validateParentalLockUseCase.execute(apiKey, identityToken, epochMs, encLock);
    }

    async getParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encPassword: string
    ): Promise<IGetParentalLockResponse['data']> {
        return await this.getParentalLockUseCase.execute(apiKey, identityToken, epochMs, encPassword);
    }

    async updateParentalLock(
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encPassword: string,
        newEncLock: string
    ): Promise<IUpdateParentalLockResponse['data']> {
        return await this.updateParentalLockUseCase.execute(apiKey, identityToken, epochMs, encPassword, newEncLock);
    }

    async resetParentalLockGenerateOTP(
        apiKey: string,
        identityToken: string,
        username: string,
        lang: string
    ): Promise<IB2CForgotParentalLockStep1Response['data']> {
        return await this.resetParentalLockOTPUseCase.generateOTP(apiKey, identityToken, username, lang);
    }

    async resetParentalLockVerifyOTP(
        apiKey: string,
        identityToken: string,
        otp: string,
        verificationGuid: string
    ): Promise<IResetParentalLockVerifyOTPResponse['data']> {
        return await this.resetParentalLockOTPUseCase.verifyOTP(apiKey, identityToken, otp, verificationGuid);
    }

    async resetParentalLockWithOTP(
        apiKey: string,
        identityToken: string,
        pin: string,
        options?: { verificationGuid?: string; currentParentalLockPin?: string }
    ): Promise<IUpdateParentalLockResponse['data']> {
        return await this.resetParentalLockOTPUseCase.resetWithOTP(apiKey, identityToken, pin, options);
    }

    async validatePassword(
        authToken: string,
        apiKey: string,
        identityToken: string,
        epochMs: string,
        encPw: string,
        username: string
    ): Promise<IValidatePasswordResponse['data']> {
        return await this.validatePasswordUseCase.execute(authToken, apiKey, identityToken, epochMs, encPw, username);
    }
}
